import { format } from "date-fns";
import MarkdownRenderer from "../../../../components/agno/MarkdownRenderer";
import { Badge } from "../../../../components/ui/badge";
import { Card, CardContent, CardFooter, CardHeader } from "../../../../components/ui/card";
import { cn } from "../../../../lib/utils";
import type { SignalDocument } from "../../../../services/signalDocuments/signalDocumentsEndpoints.interface";
import DocumentFeedbackControls from "./DocumentFeedbackControls";

type Props = {
  document: SignalDocument;
  workspaceId: string;
};

const HEALTH_STATUS_STYLES: Record<string, string> = {
  healthy: "border-emerald-200 bg-emerald-50 text-emerald-700",
  at_risk: "border-amber-200 bg-amber-50 text-amber-700",
  critical: "border-rose-200 bg-rose-50 text-rose-700",
};

const parseStructuredContent = (
  content: SignalDocument["structured_result"] | SignalDocument["content_structured"],
) => {
  if (!content) return null;
  if (typeof content === "string") {
    try {
      const parsed = JSON.parse(content);
      return parsed && typeof parsed === "object" ? parsed as Record<string, any> : null;
    } catch {
      return null;
    }
  }
  return typeof content === "object" ? content : null;
};

const formatScopeType = (scopeType?: string | null) => {
  if (!scopeType) return null;
  return scopeType.replace(/_/g, " ");
};

const SignalDocumentCard = ({ document: doc, workspaceId }: Props) => {
  const timestamp = doc.created_at
    ? format(new Date(doc.created_at), "MMM d, yyyy h:mm a")
    : "";
  const structuredContent = parseStructuredContent(doc.structured_result ?? doc.content_structured);
  const healthStatus = typeof structuredContent?.health_status === "string"
    ? structuredContent.health_status
    : null;
  const scopeTypeLabel = formatScopeType(doc.health_model_scope_type);
  const healthModelDetail = doc.health_model_scope_type === "general"
    ? "Fallback only"
    : doc.health_model_scope_value;

  return (
    <Card className="mb-3">
      <CardHeader className="space-y-3 pb-4">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-foreground">
              {doc.title || "AI document"}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {doc.doc_version ? (
                <Badge variant="outline" className="h-5 text-[10px]">
                  v{doc.doc_version}
                </Badge>
              ) : null}
              {doc.health_model_label ? (
                <Badge variant="outline" className="h-5 text-[10px]">
                  {doc.health_model_label}
                </Badge>
              ) : null}
              {scopeTypeLabel ? (
                <Badge variant="outline" className="h-5 text-[10px] capitalize">
                  {scopeTypeLabel}
                </Badge>
              ) : null}
              {healthStatus ? (
                <Badge
                  variant="outline"
                  className={cn("h-5 border text-[10px] capitalize", HEALTH_STATUS_STYLES[healthStatus] || "border-border-chip")}
                >
                  {healthStatus.replace(/_/g, " ")}
                </Badge>
              ) : null}
              {doc.skills_used?.map((skill) => (
                <Badge
                  key={skill.skill_id || skill.skill_name}
                  variant="outline"
                  className="h-5 text-[10px]"
                >
                  {skill.skill_name || skill.skill_id}
                </Badge>
              ))}
            </div>
            {doc.health_model_source_label || doc.health_model_scope_value ? (
              <p className="mt-2 text-[11px] text-muted-foreground">
                {doc.health_model_source_label || "Health model"}
                {healthModelDetail ? ` · ${healthModelDetail}` : ""}
              </p>
            ) : null}
          </div>
          <p className="text-[11px] text-muted-foreground">{timestamp}</p>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <MarkdownRenderer>{doc.content_markdown || "_No analysis generated._"}</MarkdownRenderer>
      </CardContent>

      <CardFooter className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
        <DocumentFeedbackControls
          documentId={doc.id}
          workspaceId={workspaceId}
        />
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
          {typeof doc.tokens_used === "number" ? <span>{doc.tokens_used} tokens</span> : null}
          {typeof doc.generation_time_ms === "number" ? <span>{doc.generation_time_ms}ms</span> : null}
        </div>
      </CardFooter>
    </Card>
  );
};

export default SignalDocumentCard;
